export interface Plan {
  id: string;
  name: string;
  price: string;
  priceValue: number; // Numeric value for calculations
  period: string; // Display text like "/month"
  billingFrequency: 'monthly' | 'weekly'; // For logic
  monthlyProjectCredits: number | null; // null = unlimited or pay-as-you-go (no monthly bundle)
  description: string;
  features: string[];
  priceId: string;
  popular?: boolean;
  buttonText?: string;
}

/**
 * @deprecated Use useBilling() hook from BillingContext to fetch dynamic tier data from Supabase instead.
 * This array is no longer used and will be removed in a future release.
 */
export const SUBSCRIPTION_PLANS: Plan[] = [
  {
    id: "bronze",
    name: "Bronze",
    price: "£200",
    priceValue: 200,
    period: "/month",
    billingFrequency: 'monthly',
    monthlyProjectCredits: 1,
    description: "1 service credit per month. Includes access to Quantis with a 30-day trial.",
    features: [
      "1 service credit / month",
      "Full Quantis platform access",
      "Standard support",
      "30-day trial",
      "90-day minimum term",
    ],
    priceId: "price_1T1ZcHS58xR6d9dY0T0vCiOw",
    buttonText: "Choose Bronze",
  },
  {
    id: "silver",
    name: "Silver",
    price: "£450",
    priceValue: 450,
    period: "/month",
    billingFrequency: 'monthly',
    monthlyProjectCredits: 3,
    description: "3 service credits per month. Includes standard Quantis access and expert contract advice.",
    features: [
      "3 service credits / month",
      "Full Quantis platform access",
      "Expert contract advice",
      "Priority email support",
      "90-day minimum term",
    ],
    priceId: "price_1SwKGIS58xR6d9dYwg3fQ3iB",
    buttonText: "Choose Silver",
  },
  {
    id: "gold",
    name: "Gold",
    price: "£750",
    priceValue: 750,
    period: "/month",
    billingFrequency: 'monthly',
    monthlyProjectCredits: 5,
    description: "5 service credits per month. Includes standard Quantis access and expert contract advice.",
    features: [
      "5 service credits / month",
      "Full Quantis platform access",
      "Expert contract advice",
      "Priority email support",
      "90-day minimum term",
    ],
    priceId: "price_1SwKFfS58xR6d9dY5roCGkIV",
    popular: true,
    buttonText: "Choose Gold",
  },
  {
    id: "platinum",
    name: "Platinum",
    price: "£1,100",
    priceValue: 1100,
    period: "/week",
    billingFrequency: 'weekly',
    monthlyProjectCredits: null,
    description: "Unlimited service credits and full Quantity Surveying support. Billed weekly.",
    features: [
      "Unlimited service credits",
      "Full Quantity Surveying support",
      "Full Quantis platform access",
      "Dedicated account manager",
      "Exempt from fair-use policy",
    ],
    priceId: "price_1SwKDwS58xR6d9dYFzesaHGB",
    buttonText: "Choose Platinum",
  },
  {
    id: "ad-hoc",
    name: "Ad Hoc",
    price: "from £110",
    priceValue: 110,
    period: "/hour",
    billingFrequency: 'weekly',
    monthlyProjectCredits: null,
    description: "Flexible hourly, daily, or weekly billing for non-retainer clients. Full platform access with no minimum term.",
    features: [
      "Hourly: £110 + VAT (15-min increments)",
      "Day Rate: £675 + VAT per day",
      "Weekly: £1,100 + VAT (£220/day equivalent)",
      "Full Quantis platform access",
      "Email support",
      "No minimum term",
    ],
    priceId: "price_1TWgg0S58xR6d9dYY6VKbXkU", // Weekly rate; hourly: price_1TWgfuS58xR6d9dYI3necNnu, daily: price_1TWgfxS58xR6d9dYG9q3ISbe
    buttonText: "Choose Ad Hoc",
  }
];

export const normalizePlanId = (id?: string | null): string | undefined => {
  if (!id) return undefined;
  const normalized = id.toLowerCase();
  if (normalized === 'essential') return 'silver';
  if (normalized === 'professional') return 'gold';
  if (normalized === 'enterprise') return 'platinum';
  return normalized;
};

/**
 * @deprecated Use useBilling() hook's getTierById() method to fetch dynamic tier data instead.
 * This function relies on hardcoded SUBSCRIPTION_PLANS and will be removed.
 */
export const getPlanById = (id: string) => SUBSCRIPTION_PLANS.find(p => p.id === normalizePlanId(id));

